import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { ScrollArea } from '../components/ui/scroll-area';
import { Checkbox } from '../components/ui/checkbox';
import { LICENSE_AGREEMENT, EXAMPLE_DOCUMENTS, PRIVACY_POLICY, USER_AGREEMENT, MEDICAL_DATA_CONSENT } from '../mock';
import { X, ArrowDown, CheckCircle, Zap, FileText, Activity, Brain, ShieldCheck, ChevronLeft, ChevronRight, Plus, Mail, MapPin, Building2, Scale, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import BlackHoleWebGL from '../components/BlackHoleWebGL';
import AcceleratingBlackHole from '../components/AcceleratingBlackHole';
import DeepAnalysisModal from '../components/DeepAnalysisModal';
import DocumentExampleModal from '../components/DocumentExampleModal';
import MedicalProtocolsSection from '../components/MedicalProtocolsSection';
import LegalModal from '../components/LegalModal';
import DataDeletionRequestModal from '../components/DataDeletionRequestModal';
import { medicalAPI } from '../services/api';

// Компонент для анимации счетчика
const AnimatedCounter = ({ end, duration = 2000, suffix = '' }) => {
  const [count, setCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const counterRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (counterRef.current) {
      observer.observe(counterRef.current);
    }

    return () => {
      if (counterRef.current) {
        observer.unobserve(counterRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!isVisible) return;

    let startTime = null;
    const endValue = parseFloat(end);

    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);

      const easeOutQuad = progress * (2 - progress);
      setCount(Math.floor(easeOutQuad * endValue));

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        setCount(endValue);
      }
    };

    requestAnimationFrame(animate);
  }, [isVisible, end, duration]);

  return (
    <span ref={counterRef}>
      {count}{suffix}
    </span>
  );
};

// Компонент для анимированной секции с Intersection Observer
const AnimatedSection = ({ children, className = '', animationType = 'slide-bottom', delay = 0 }) => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, [isVisible]);

  const animationClass = isVisible 
    ? `animate-slide-${animationType.split('-')[1] || 'bottom'} animate-delay-${delay}`
    : 'opacity-0';

  return (
    <div 
      ref={sectionRef} 
      className={`${className} ${animationClass}`}
      style={{ opacity: isVisible ? undefined : 0 }}
    >
      {children}
    </div>
  );
};

const LandingPage = () => {
  const [showAgreement, setShowAgreement] = useState(false);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(true);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [isAbsorbingToBlackHole, setIsAbsorbingToBlackHole] = useState(false); // Эффект всасывания в черную дыру
  const [showAnalysisModal, setShowAnalysisModal] = useState(false); // Модальное окно для загрузки анализов
  const [agreementAction, setAgreementAction] = useState(null); // 'analysis' или 'auth'
  const [reviews, setReviews] = useState([]); // Отзывы
  const [currentReview, setCurrentReview] = useState(0); // Текущий отзыв в карусели
  const [showReviewForm, setShowReviewForm] = useState(false); // Форма добавления отзыва
  const [reviewForm, setReviewForm] = useState({ name: '', email: '', rating: 5, comment: '' });
  const [currentMetric, setCurrentMetric] = useState(0); // Текущий показатель: 0 - Точность, 1 - Скорость, 2 - Охват
  const [selectedDocument, setSelectedDocument] = useState(null); // Выбранный документ для модального окна
  const [legalDoc, setLegalDoc] = useState(null); // {document, title} — выбранный правовой документ для модалки
  const [showDataDeletionModal, setShowDataDeletionModal] = useState(false); // Модалка запроса на удаление данных
  const [isOnHeroScreen, setIsOnHeroScreen] = useState(true); // Находимся ли мы на первом экране
  const scrollAreaRef = useRef(null);
  const aboutSectionRef = useRef(null);
  const carouselRef = useRef(null); // Ссылка на карусель
  const mainContentRef = useRef(null); // Ссылка на основной контент для отслеживания скролла
  const navigate = useNavigate();

  useEffect(() => {
    if (isAbsorbingToBlackHole) {
      // Через 3 секунды после начала всасывания переходим на страницу входа
      const timer = setTimeout(() => {
        navigate('/auth');
      }, 3000);
      
      return () => clearTimeout(timer);
    }
  }, [isAbsorbingToBlackHole, navigate]);

  // Автопереключение метрик (Точность, Скорость, Охват)
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentMetric((prev) => (prev + 1) % 3);
    }, 5000); // Переключение каждые 5 секунд
    
    return () => clearInterval(timer);
  }, []);

  // Отслеживание скролла для определения, на каком экране мы находимся
  useEffect(() => {
    const handleScroll = () => {
      if (mainContentRef.current) {
        const scrollTop = mainContentRef.current.scrollTop;
        const windowHeight = window.innerHeight;
        // Если прокрутили больше чем на 50% первого экрана, считаем что ушли с hero
        setIsOnHeroScreen(scrollTop < windowHeight * 0.5);
      }
    };

    const contentElement = mainContentRef.current;
    if (contentElement) {
      contentElement.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (contentElement) {
        contentElement.removeEventListener('scroll', handleScroll);
      }
    };
  }, []);

  // Загрузка отзывов
  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const response = await medicalAPI.getReviews();
        console.log('Reviews response:', response);
        const reviewsData = response.reviews || [];
        console.log('Reviews data:', reviewsData);
        setReviews(reviewsData);
      } catch (error) {
        console.error('Error fetching reviews:', error);
      }
    };
    fetchReviews();
  }, []);

  // Обработка отправки отзыва
  const handleSubmitReview = async (e) => {
    e.preventDefault();
    try {
      const response = await medicalAPI.createReview(reviewForm);
      setReviews([response.review, ...reviews]);
      setReviewForm({ name: '', email: '', rating: 5, comment: '' });
      setShowReviewForm(false);
      alert('Спасибо за ваш отзыв!');
    } catch (error) {
      console.error('Error submitting review:', error);
      alert('Ошибка при отправке отзыва. Попробуйте позже.');
    }
  };

  const handleScrollCheck = (e) => {
    const element = e.currentTarget;
    const scrollDiv = element.querySelector('[data-radix-scroll-area-viewport]');
    if (scrollDiv) {
      const isAtBottom = scrollDiv.scrollHeight - scrollDiv.scrollTop <= scrollDiv.clientHeight + 50;
      if (isAtBottom && !hasScrolledToBottom) {
        setHasScrolledToBottom(true);
      }
    }
  };

  const handleAgreeClick = (action = 'auth') => {
    // Не прокручиваем страницу - модал откроется на текущей позиции
    setAgreementAction(action);
    setShowAgreement(true);
  };

  const handleConfirmAgreement = () => {
    if (!agreedToTerms) return;
    
    setShowAgreement(false);
    
    // Если это была кнопка "Расшифровать анализ", открываем окно загрузки анализов
    if (agreementAction === 'analysis') {
      setShowAnalysisModal(true);
    } else {
      // Иначе запускаем эффект всасывания в черную дыру
      setIsAbsorbingToBlackHole(true);
    }
  };

  const scrollToCarousel = () => {
    carouselRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="relative w-full h-screen overflow-hidden bg-black">
      {/* Фоновая черная дыра - ВСЕГДА видна, меняется скорость роста при всасывании */}
      <div className="absolute top-0 left-0 w-full h-full z-0">
        <BlackHoleWebGL accelerate={isAbsorbingToBlackHole} interactive={isOnHeroScreen} />
      </div>

      {/* Overlay для затемнения контента - исчезает при всасывании */}
      <div 
        className={`absolute inset-0 bg-black/40 z-10 transition-opacity duration-1000 ${
          isAbsorbingToBlackHole ? 'opacity-0' : 'opacity-100'
        }`} 
      />

      {/* Начальный контент - исчезает при всасывании */}
      <div 
        ref={mainContentRef}
        className={`relative z-20 w-full h-full overflow-y-auto overflow-x-hidden transition-opacity duration-1000 ${
          isAbsorbingToBlackHole ? 'opacity-0' : 'opacity-100'
        }`}
      >
          {/* Hero секция */}
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="text-center space-y-6 md:space-y-8 animate-fade-in px-4">
              <h1 className="text-4xl sm:text-5xl md:text-7xl font-light text-white tracking-wider">
                AIMedGuru
              </h1>
              <p className="text-lg sm:text-xl md:text-2xl text-white/80 font-light max-w-md mx-auto">
                Медицинский ИИ-ассистент
              </p>
              <p className="text-lg sm:text-xl md:text-2xl text-cyan-400/90 font-light max-w-lg mx-auto mt-2">
                Наша миссия — сделать медицину доступной каждому
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mt-8 md:mt-12">
                <Button
                  onClick={scrollToCarousel}
                  className="glass-btn text-white px-6 sm:px-8 py-4 sm:py-6 text-base sm:text-lg rounded-full hover:scale-105"
                  data-testid="about-btn"
                >
                  О нас
                </Button>
                <Button
                  onClick={() => setIsAbsorbingToBlackHole(true)}
                  className="glass-btn text-white px-6 sm:px-8 py-4 sm:py-6 text-base sm:text-lg rounded-full hover:scale-105"
                  data-testid="login-btn"
                >
                  Войти
                </Button>
              </div>
              <div className="mt-12 animate-bounce">
                <ArrowDown className="w-6 h-6 text-white/60 mx-auto" />
              </div>
            </div>
          </div>

          {/* CTA секция */}
          <div ref={carouselRef} className="relative py-20 md:py-32 px-4">
            <div className="relative z-10 max-w-4xl mx-auto text-center space-y-8">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-light text-white leading-tight">
                Онлайн расшифровка анализов и снимков
              </h2>
              <p className="text-base md:text-lg lg:text-xl text-white/80 leading-relaxed max-w-3xl mx-auto">
                Получите профессиональную расшифровку ваших медицинских анализов, МРТ, КТ, УЗИ и других снимков с помощью искусственного интеллекта за считанные минуты.
              </p>
              
              <div className="space-y-4 flex flex-col items-center pt-4">
                <Button
                  onClick={() => navigate('/auth')}
                  className="glass-btn-accent text-white border-0 px-6 sm:px-10 py-5 sm:py-7 text-base sm:text-lg rounded-full transition-all duration-300 hover:scale-105"
                  data-testid="decode-analysis-btn"
                >
                  <Zap className="w-5 h-5 sm:w-6 sm:h-6 mr-2 sm:mr-3" />
                  Начать бесплатно
                </Button>
                <p className="text-emerald-400 font-medium text-base sm:text-lg text-center">
                  Полностью бесплатно на время бета-тестирования
                </p>
              </div>
            </div>
          </div>

          {/* Примеры расшифровки */}
          <div className="relative py-16 px-4">
            <div className="relative z-10 max-w-4xl mx-auto">
              <div className="text-center space-y-6">
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-light text-white mb-3">
                  Примеры расшифровки
                </h2>
                <Button
                  onClick={() => setSelectedDocument(EXAMPLE_DOCUMENTS[0])}
                  className="glass-btn text-white px-6 sm:px-10 py-5 sm:py-7 text-base sm:text-lg rounded-full hover:scale-105"
                  data-testid="example-doc-btn"
                >
                  <FileText className="w-5 h-5 sm:w-6 sm:h-6 mr-2 sm:mr-3" />
                  Посмотреть образец
                </Button>
              </div>
            </div>
          </div>

          {/* About секция */}
          <div ref={aboutSectionRef} className="relative px-4 py-16 md:py-24">
            <div className="relative z-10 max-w-6xl mx-auto space-y-12 md:space-y-16">
              
              {/* Заголовок секции */}
              <div className="text-center space-y-4">
                <h2 className="text-3xl sm:text-4xl md:text-5xl font-light text-white">
                  О AIMedGuru
                </h2>
                <p className="text-base sm:text-lg text-white/70 max-w-2xl mx-auto">
                  Передовой искусственный интеллект для медицинской диагностики
                </p>
              </div>

              {/* Описание AI */}
              <div className="liquid-glass-card rounded-3xl p-6 md:p-8 space-y-4 animate-slide-left animate-delay-1">
                <div className="relative z-10 flex items-center gap-3 mb-4">
                  <div className="liquid-glass-icon rounded-2xl w-12 h-12 flex items-center justify-center">
                    <Brain className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl md:text-2xl font-light text-white">
                    Технология и обучение
                  </h3>
                </div>
                <p className="relative z-10 text-sm md:text-base text-white/80 leading-relaxed">
                  AIMedGuru разработан на основе передовых нейронных сетей и обучен на миллионах медицинских документов, 
                  научных публикаций и клинических случаев. Наша система постоянно совершенствуется, анализируя новейшие 
                  исследования и медицинские данные.
                </p>
                <p className="relative z-10 text-sm md:text-base text-white/80 leading-relaxed">
                  Качество результатов подтверждено тысячами успешных анализов. AI способен обрабатывать сложные медицинские 
                  данные, выявлять скрытые закономерности и предоставлять детальные рекомендации с высокой точностью.
                </p>
              </div>

              {/* Три информационных блока */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
                {/* Расшифровка лабораторных анализов */}
                <div className="liquid-glass-card rounded-3xl p-6 md:p-8 space-y-6 animate-slide-left animate-delay-2">
                  <div className="relative z-10 flex items-center gap-3 mb-4">
                    <div className="liquid-glass-icon rounded-2xl w-12 h-12 flex items-center justify-center">
                      <FileText className="w-6 h-6 text-blue-400" />
                    </div>
                    <h3 className="text-xl md:text-2xl font-light text-white">
                      Расшифровка лабораторных анализов
                    </h3>
                  </div>
                  <p className="relative z-10 text-sm md:text-base text-white/80 leading-relaxed">
                    ИИ распознаёт все основные типы анализов: кровь (общий, биохимия, гормоны, антитела), моча, кал, мазки, иммунология, онкомаркеры, витамины, аллергологические панели.
                  </p>
                  <div className="relative z-10 space-y-3">
                    <h4 className="text-base font-medium text-white">Что вы получаете:</h4>
                    <ul className="space-y-2">
                      <li className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-white/80">Простое объяснение каждого показателя</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-white/80">Варианты возможных причин отклонений</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-white/80">Предупреждение о рисках</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-white/80">Что стоит обсудить с врачом</span>
                      </li>
                    </ul>
                  </div>
                </div>

                {/* Интерпретация медицинских изображений */}
                <div className="liquid-glass-card rounded-3xl p-6 md:p-8 space-y-6 animate-slide-bottom animate-delay-3">
                  <div className="relative z-10 flex items-center gap-3 mb-4">
                    <div className="liquid-glass-icon rounded-2xl w-12 h-12 flex items-center justify-center">
                      <Activity className="w-6 h-6 text-purple-400" />
                    </div>
                    <h3 className="text-xl md:text-2xl font-light text-white">
                      Интерпретация медицинских изображений
                    </h3>
                  </div>
                  <p className="relative z-10 text-sm md:text-base text-white/80 leading-relaxed">
                    ИИ анализирует рентген снимки, КТ, МРТ, дерматологические фото, патологические слайды, офтальмологические и стоматологические изображения.
                  </p>
                  <div className="relative z-10 space-y-3">
                    <h4 className="text-base font-medium text-white">Что вы получаете:</h4>
                    <ul className="space-y-2">
                      <li className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-white/80">Автоматическое распознавание аномалий</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-white/80">Подробный текстовый отчёт</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-white/80">Подсказки по возможным патологиям</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-white/80">Пояснение человеческим языком</span>
                      </li>
                    </ul>
                  </div>
                </div>

                {/* Обработка медицинских текстов */}
                <div className="liquid-glass-card rounded-3xl p-6 md:p-8 space-y-6 animate-slide-right animate-delay-4">
                  <div className="relative z-10 flex items-center gap-3 mb-4">
                    <div className="liquid-glass-icon rounded-2xl w-12 h-12 flex items-center justify-center">
                      <Brain className="w-6 h-6 text-cyan-400" />
                    </div>
                    <h3 className="text-xl md:text-2xl font-light text-white">
                      Обработка медицинских текстов
                    </h3>
                  </div>
                  <p className="relative z-10 text-sm md:text-base text-white/80 leading-relaxed">
                    ИИ понимает смысл медицинских заключений, выписок и рецептов, анализирует диагнозы, строит логические связи между симптомами и данными.
                  </p>
                  <div className="relative z-10 space-y-3">
                    <h4 className="text-base font-medium text-white">Что вы получаете:</h4>
                    <ul className="space-y-2">
                      <li className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-white/80">Сжатое и понятное резюме</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-white/80">Ответы на ваши вопросы</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-white/80">Предварительная интерпретация</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-white/80">Подготовка к визиту врача</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Как это работает */}
              <div className="liquid-glass-card rounded-3xl p-6 md:p-8 space-y-6">
                <div className="relative z-10 flex items-center gap-3 mb-4">
                  <div className="liquid-glass-icon rounded-2xl w-12 h-12 flex items-center justify-center">
                    <ShieldCheck className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl md:text-2xl font-light text-white">
                    Как это работает
                  </h3>
                </div>
                <div className="relative z-10 grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <div className="liquid-glass-icon rounded-xl w-10 h-10 flex items-center justify-center flex-shrink-0">
                        <span className="text-white font-medium">1</span>
                      </div>
                      <div>
                        <h4 className="text-base font-medium text-white mb-1">Загрузка данных</h4>
                        <p className="text-sm text-white/70">
                          Загрузите медицинские документы: анализы крови, результаты МРТ, КТ, УЗИ, фотографии кожных проявлений и другие медицинские данные в любом формате.
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="liquid-glass-icon rounded-xl w-10 h-10 flex items-center justify-center flex-shrink-0">
                        <span className="text-white font-medium">2</span>
                      </div>
                      <div>
                        <h4 className="text-base font-medium text-white mb-1">AI анализ</h4>
                        <p className="text-sm text-white/70">
                          Искусственный интеллект обрабатывает все данные, сопоставляя с миллионами клинических случаев и последними медицинскими исследованиями.
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <div className="liquid-glass-icon rounded-xl w-10 h-10 flex items-center justify-center flex-shrink-0">
                        <span className="text-white font-medium">3</span>
                      </div>
                      <div>
                        <h4 className="text-base font-medium text-white mb-1">Детальный отчет</h4>
                        <p className="text-sm text-white/70">
                          Получите комплексный анализ с выявленными отклонениями, возможными диагнозами и персонализированными рекомендациями.
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="liquid-glass-icon rounded-xl w-10 h-10 flex items-center justify-center flex-shrink-0">
                        <span className="text-white font-medium">4</span>
                      </div>
                      <div>
                        <h4 className="text-base font-medium text-white mb-1">Консультация</h4>
                        <p className="text-sm text-white/70">
                          Обсудите результаты с AI-ассистентом, задайте вопросы и получите разъяснения в режиме диалога.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Глобальная научная база AiMedGuru */}
              <MedicalProtocolsSection />

              {/* Типы документов */}
              <div className="liquid-glass-card rounded-3xl p-6 md:p-8 space-y-4">
                <div className="relative z-10 flex items-center gap-3 mb-4">
                  <div className="liquid-glass-icon rounded-2xl w-12 h-12 flex items-center justify-center">
                    <FileText className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl md:text-2xl font-light text-white">
                    Поддерживаемые документы
                  </h3>
                </div>
                <div className="relative z-10 grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
                  {[
                    'Анализы крови',
                    'МРТ снимки',
                    'КТ сканы',
                    'УЗИ изображения',
                    'Рентген',
                    'ЭКГ данные',
                    'Фото симптомов',
                    'PDF отчеты'
                  ].map((type, index) => (
                    <div key={index} className="liquid-glass-icon rounded-2xl p-3 sm:p-4 text-center hover:scale-[1.02] transition-all">
                      <p className="text-sm text-white/80">{type}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Заболевания */}
              <div className="liquid-glass-card rounded-3xl p-6 md:p-8 space-y-4">
                <h3 className="relative z-10 text-xl md:text-2xl font-light text-white mb-4">
                  Диапазон анализируемых заболеваний
                </h3>
                <p className="relative z-10 text-sm md:text-base text-white/80 leading-relaxed mb-4">
                  AIMedGuru способен выявлять и анализировать широкий спектр заболеваний и состояний:
                </p>
                <div className="relative z-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {[
                    'Сердечно-сосудистые заболевания',
                    'Эндокринные нарушения',
                    'Онкологические маркеры',
                    'Инфекционные заболевания',
                    'Аутоиммунные состояния',
                    'Метаболические расстройства',
                    'Неврологические патологии',
                    'Дерматологические проблемы',
                    'Гастроэнтерологические заболевания',
                    'Почечные дисфункции',
                    'Печеночные патологии',
                    'Гематологические отклонения'
                  ].map((disease, index) => (
                    <div key={index} className="flex items-center gap-2 liquid-glass-icon rounded-2xl p-3 hover:scale-[1.01] transition-all">
                      <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0" />
                      <span className="text-sm text-white/80">{disease}</span>
                    </div>
                  ))}
                </div>
                <p className="relative z-10 text-sm text-white/60 italic mt-4">
                  * и многие другие состояния. Система постоянно обучается и расширяет базу знаний.
                </p>
              </div>

              {/* Конфиденциальность и безопасность */}
              <div className="liquid-glass-card rounded-3xl p-6 md:p-8 space-y-6">
                <div className="relative z-10 text-center space-y-4 mb-6">
                  <h3 className="text-2xl md:text-3xl font-light text-white">
                    Конфиденциальность и безопасность
                  </h3>
                  <p className="text-base text-white/70 max-w-2xl mx-auto">
                    Ваши данные надежно защищены
                  </p>
                </div>

                <div className="relative z-10 grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                  <div className="liquid-glass-card rounded-3xl p-5 sm:p-6 space-y-3 hover:scale-[1.01] transition-all">
                    <div className="relative z-10 flex items-center gap-3">
                      <div className="liquid-glass-icon rounded-2xl w-12 h-12 flex items-center justify-center">
                        <ShieldCheck className="w-6 h-6 text-green-400" />
                      </div>
                      <h4 className="text-lg font-medium text-white">Шифрование данных</h4>
                    </div>
                    <p className="text-sm text-white/70 leading-relaxed">
                      Все загружаемые медицинские документы и личные данные шифруются с использованием современных алгоритмов. 
                      Ваша информация защищена на уровне банковских систем.
                    </p>
                  </div>

                  <div className="liquid-glass-card rounded-3xl p-5 sm:p-6 space-y-3 hover:scale-[1.01] transition-all">
                    <div className="relative z-10 flex items-center gap-3">
                      <div className="liquid-glass-icon rounded-2xl w-12 h-12 flex items-center justify-center">
                        <ShieldCheck className="w-6 h-6 text-blue-400" />
                      </div>
                      <h4 className="text-lg font-medium text-white">Анонимность</h4>
                    </div>
                    <p className="relative z-10 text-sm text-white/70 leading-relaxed">
                      Мы не передаем ваши данные третьим лицам. Анализ проводится конфиденциально, 
                      без сохранения персональной информации на сторонних серверах.
                    </p>
                  </div>

                  <div className="liquid-glass-card rounded-3xl p-5 sm:p-6 space-y-3 hover:scale-[1.01] transition-all">
                    <div className="relative z-10 flex items-center gap-3">
                      <div className="liquid-glass-icon rounded-2xl w-12 h-12 flex items-center justify-center">
                        <ShieldCheck className="w-6 h-6 text-purple-400" />
                      </div>
                      <h4 className="text-lg font-medium text-white">Соответствие стандартам</h4>
                    </div>
                    <p className="relative z-10 text-sm text-white/70 leading-relaxed">
                      Наш сервис соответствует международным стандартам безопасности GDPR и локальным требованиям 
                      по защите медицинских данных.
                    </p>
                  </div>

                  <div className="liquid-glass-card rounded-3xl p-5 sm:p-6 space-y-3 hover:scale-[1.01] transition-all">
                    <div className="relative z-10 flex items-center gap-3">
                      <div className="liquid-glass-icon rounded-2xl w-12 h-12 flex items-center justify-center">
                        <ShieldCheck className="w-6 h-6 text-orange-400" />
                      </div>
                      <h4 className="text-lg font-medium text-white">Контроль данных</h4>
                    </div>
                    <p className="relative z-10 text-sm text-white/70 leading-relaxed">
                      Вы можете удалить свои данные в любой момент. Все результаты анализов хранятся 
                      только в вашем аккаунте и доступны только вам.
                    </p>
                  </div>
                </div>
              </div>

              {/* FAQ */}
              <div className="liquid-glass-card rounded-3xl p-6 md:p-8 space-y-6">
                <div className="relative z-10 text-center space-y-4 mb-6">
                  <h3 className="text-2xl md:text-3xl font-light text-white">
                    Часто задаваемые вопросы
                  </h3>
                </div>

                <div className="relative z-10 space-y-4">
                  <div className="liquid-glass-card rounded-3xl p-5 sm:p-6 space-y-3">
                    <h4 className="relative z-10 text-base font-medium text-white">
                      Заменяет ли AI консультацию врача?
                    </h4>
                    <p className="relative z-10 text-sm text-white/70 leading-relaxed">
                      Нет, AIMedGuru не заменяет врача. Мы предоставляем информационную поддержку, помогая вам лучше 
                      понять результаты анализов и подготовиться к визиту к специалисту. Окончательный диагноз и лечение 
                      должен назначать квалифицированный врач.
                    </p>
                  </div>

                  <div className="liquid-glass-card rounded-3xl p-5 sm:p-6 space-y-3">
                    <h4 className="relative z-10 text-base font-medium text-white">
                      Насколько точен анализ AI?
                    </h4>
                    <p className="relative z-10 text-sm text-white/70 leading-relaxed">
                      Наш AI обучен на миллионах медицинских документов и показывает точность 96.5% в интерпретации 
                      стандартных анализов. Однако мы рекомендуем всегда консультироваться с врачом для подтверждения результатов.
                    </p>
                  </div>

                  <div className="liquid-glass-card rounded-3xl p-5 sm:p-6 space-y-3">
                    <h4 className="relative z-10 text-base font-medium text-white">
                      Сколько времени занимает анализ?
                    </h4>
                    <p className="relative z-10 text-sm text-white/70 leading-relaxed">
                      Базовый анализ занимает около 2 минут. Глубокий анализ с детальными рекомендациями может занять 
                      до 5 минут в зависимости от объема данных.
                    </p>
                  </div>

                  <div className="liquid-glass-card rounded-3xl p-5 sm:p-6 space-y-3">
                    <h4 className="relative z-10 text-base font-medium text-white">
                      Какие форматы файлов поддерживаются?
                    </h4>
                    <p className="relative z-10 text-sm text-white/70 leading-relaxed">
                      Мы поддерживаем PDF, JPG, PNG, DICOM (для медицинских снимков) и текстовые форматы. 
                      Вы можете загрузить как сканы бумажных документов, так и цифровые файлы.
                    </p>
                  </div>

                  <div className="liquid-glass-card rounded-3xl p-5 sm:p-6 space-y-3">
                    <h4 className="relative z-10 text-base font-medium text-white">
                      Могу ли я использовать сервис для всей семьи?
                    </h4>
                    <p className="relative z-10 text-sm text-white/70 leading-relaxed">
                      Да! Во время бета-тестирования вы можете создать отдельные профили для членов вашей семьи. 
                      Каждый получит доступ к полному функционалу платформы с собственной историей анализов и рекомендациями.
                    </p>
                  </div>
                </div>
              </div>

              {/* Бесплатное тестирование */}
              <div className="liquid-glass-card rounded-3xl p-8 md:p-12 space-y-8 relative overflow-hidden">
                
                <div className="relative z-10">
                  {/* Заголовок */}
                  <div className="text-center space-y-6 mb-10">
                    <div className="inline-flex items-center gap-2 liquid-glass-icon rounded-full px-6 py-2">
                      <Zap className="w-5 h-5 text-emerald-400" />
                      <span className="text-emerald-300 font-medium text-sm">Открытое бета-тестирование</span>
                    </div>
                    
                    <h3 className="text-3xl md:text-5xl font-light text-white leading-tight">
                      Полностью <span className="text-emerald-400 font-medium">бесплатно</span> на время тестирования
                    </h3>
                    
                    <p className="text-lg md:text-xl text-white/80 max-w-3xl mx-auto leading-relaxed">
                      Мы разрабатываем революционную платформу для анализа медицинских данных с помощью искусственного интеллекта. 
                      Присоединяйтесь к бета-тестированию и получите <span className="text-emerald-400 font-semibold">неограниченный доступ</span> ко всем функциям абсолютно бесплатно!
                    </p>
                  </div>

                  {/* Что получают тестировщики */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
                    <div className="liquid-glass-card rounded-3xl p-6 space-y-4 hover:scale-[1.01] transition-all duration-300">
                      <div className="relative z-10 flex items-center gap-3">
                        <div className="liquid-glass-icon rounded-2xl w-12 h-12 flex items-center justify-center">
                          <Brain className="w-6 h-6 text-emerald-400" />
                        </div>
                        <h4 className="text-xl font-medium text-white">Полный доступ к AI</h4>
                      </div>
                      <ul className="space-y-3">
                        <li className="flex items-start gap-3">
                          <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                          <span className="text-white/90">Неограниченное количество анализов и расшифровок</span>
                        </li>
                        <li className="flex items-start gap-3">
                          <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                          <span className="text-white/90">Глубокий анализ всех типов медицинских документов</span>
                        </li>
                        <li className="flex items-start gap-3">
                          <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                          <span className="text-white/90">Персональный AI-ассистент доступен 24/7</span>
                        </li>
                        <li className="flex items-start gap-3">
                          <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                          <span className="text-white/90">Профессиональные PDF-отчёты для врачей</span>
                        </li>
                      </ul>
                    </div>

                    <div className="liquid-glass-card rounded-3xl p-6 space-y-4 hover:scale-[1.01] transition-all duration-300">
                      <div className="relative z-10 flex items-center gap-3">
                        <div className="liquid-glass-icon rounded-2xl w-12 h-12 flex items-center justify-center">
                          <ShieldCheck className="w-6 h-6 text-cyan-400" />
                        </div>
                        <h4 className="text-xl font-medium text-white">Привилегии тестировщика</h4>
                      </div>
                      <ul className="space-y-3">
                        <li className="flex items-start gap-3">
                          <CheckCircle className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                          <span className="text-white/90">Приоритетная техподдержка и обратная связь</span>
                        </li>
                        <li className="flex items-start gap-3">
                          <CheckCircle className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                          <span className="text-white/90">Влияние на развитие продукта вашими отзывами</span>
                        </li>
                        <li className="flex items-start gap-3">
                          <CheckCircle className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                          <span className="text-white/90">Ранний доступ к новым функциям до официального релиза</span>
                        </li>
                        <li className="flex items-start gap-3">
                          <CheckCircle className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                          <span className="text-white/90">Специальные условия при переходе на коммерческую версию</span>
                        </li>
                      </ul>
                    </div>
                  </div>

                  {/* Призыв к действию */}
                  <div className="liquid-glass-card rounded-3xl p-8 space-y-6">
                    <div className="text-center space-y-3">
                      <h4 className="text-2xl font-medium text-white">Помогите нам стать лучше</h4>
                      <p className="text-white/80 max-w-2xl mx-auto">
                        Ваш опыт и обратная связь помогут создать лучший в мире сервис для расшифровки медицинских анализов. 
                        Присоединяйтесь к сообществу ранних пользователей и получите бесплатный доступ ко всем премиум-функциям!
                      </p>
                    </div>
                    
                    <div className="flex flex-col sm:flex-row gap-4 justify-center items-center px-4">
                      <Button
                        onClick={() => navigate('/auth')}
                        className="glass-btn-accent text-white border-0 rounded-full px-6 sm:px-8 py-5 sm:py-6 text-base sm:text-lg font-medium transition-all duration-300 hover:scale-105 w-full sm:w-auto"
                        data-testid="start-testing-btn"
                      >
                        <Zap className="w-5 h-5 mr-2" />
                        Начать бесплатное тестирование
                      </Button>
                    </div>

                    <p className="text-sm text-white/60 text-center">
                      Регистрация займёт меньше минуты • Не требуется банковская карта • Отменить можно в любой момент
                    </p>
                  </div>

                  {/* Информация о тестировании */}
                  <div className="mt-8 text-center space-y-2">
                    <p className="text-white/70 text-sm">
                      <span className="text-emerald-400 font-medium">Сейчас активны:</span> <AnimatedCounter end={156} suffix=" тестировщиков" />
                    </p>
                    <p className="text-white/60 text-xs">
                      Бета-тестирование продлится до официального запуска
                    </p>
                  </div>
                </div>
              </div>

              {/* Графики сравнения */}
              <div className="liquid-glass-card rounded-3xl p-6 md:p-8 space-y-6">
                <div className="relative z-10 text-center space-y-4 mb-6">
                  <h3 className="text-2xl md:text-3xl font-light text-white">
                    Преимущества AIMedGuru
                  </h3>
                  <p className="text-base text-white/70 max-w-2xl mx-auto">
                    Сравнение с традиционными методами
                  </p>
                </div>

                <div className="relative z-10 flex justify-center gap-2 md:gap-3 mb-8 flex-wrap">
                  <button
                    onClick={() => setCurrentMetric(0)}
                    className={`px-3 py-2 md:px-6 md:py-3 rounded-full transition-all duration-300 text-sm md:text-base ${
                      currentMetric === 0
                        ? 'glass-btn border-green-400/40 text-white'
                        : 'liquid-glass-icon text-white/70'
                    }`}
                    data-testid="metric-accuracy-tab"
                  >
                    <div className="flex items-center gap-1.5 md:gap-2">
                      <CheckCircle className="w-4 h-4 md:w-5 md:h-5" />
                      <span className="font-medium">Точность</span>
                    </div>
                  </button>
                  <button
                    onClick={() => setCurrentMetric(1)}
                    className={`px-3 py-2 md:px-6 md:py-3 rounded-full transition-all duration-300 text-sm md:text-base ${
                      currentMetric === 1
                        ? 'glass-btn border-yellow-400/40 text-white'
                        : 'liquid-glass-icon text-white/70'
                    }`}
                    data-testid="metric-speed-tab"
                  >
                    <div className="flex items-center gap-1.5 md:gap-2">
                      <Zap className="w-4 h-4 md:w-5 md:h-5" />
                      <span className="font-medium">Скорость</span>
                    </div>
                  </button>
                  <button
                    onClick={() => setCurrentMetric(2)}
                    className={`px-3 py-2 md:px-6 md:py-3 rounded-full transition-all duration-300 text-sm md:text-base ${
                      currentMetric === 2
                        ? 'glass-btn border-blue-400/40 text-white'
                        : 'liquid-glass-icon text-white/70'
                    }`}
                    data-testid="metric-coverage-tab"
                  >
                    <div className="flex items-center gap-1.5 md:gap-2">
                      <Activity className="w-4 h-4 md:w-5 md:h-5" />
                      <span className="font-medium">Охват</span>
                    </div>
                  </button>
                </div>

                <div className="relative z-10 overflow-hidden">
                  <div 
                    className="flex transition-transform duration-500 ease-in-out"
                    style={{ transform: `translateX(-${currentMetric * 100}%)` }}
                  >
                    {/* График 1: Точность */}
                    <div className="w-full flex-shrink-0 px-4">
                      <div className="liquid-glass-card rounded-3xl p-8 space-y-6 max-w-2xl mx-auto">
                        <div className="relative z-10 flex items-center gap-3 justify-center">
                          <div className="liquid-glass-icon rounded-2xl w-12 h-12 flex items-center justify-center">
                            <CheckCircle className="w-6 h-6 text-green-400" />
                          </div>
                          <h4 className="text-2xl font-medium text-white">Точность</h4>
                        </div>
                        <div className="space-y-4">
                          <div className="flex justify-between items-center">
                            <span className="text-base text-white/70">AIMedGuru</span>
                            <span className="text-3xl font-bold text-white">
                              <AnimatedCounter end={96.5} suffix="%" />
                            </span>
                          </div>
                          <div className="w-full bg-white/15 rounded-full h-4 overflow-hidden">
                            <div 
                              className="bg-gradient-to-r from-green-400 to-emerald-500 h-4 rounded-full animate-chart-fill" 
                              style={{ width: '96.5%' }}
                            ></div>
                          </div>
                          <div className="flex justify-between items-center mt-6">
                            <span className="text-base text-white/70">Стандартные методы</span>
                            <span className="text-2xl text-white/60">82%</span>
                          </div>
                          <div className="w-full bg-white/15 rounded-full h-3 overflow-hidden">
                            <div 
                              className="bg-white/40 h-3 rounded-full animate-chart-fill-delayed" 
                              style={{ width: '82%' }}
                            ></div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* График 2: Скорость */}
                    <div className="w-full flex-shrink-0 px-4">
                      <div className="liquid-glass-card rounded-3xl p-8 space-y-6 max-w-2xl mx-auto">
                        <div className="relative z-10 flex items-center gap-3 justify-center">
                          <div className="liquid-glass-icon rounded-2xl w-12 h-12 flex items-center justify-center">
                            <Zap className="w-6 h-6 text-yellow-400" />
                          </div>
                          <h4 className="text-2xl font-medium text-white">Скорость</h4>
                        </div>
                        <div className="space-y-4">
                          <div className="flex justify-between items-center">
                            <span className="text-base text-white/70">AIMedGuru</span>
                            <span className="text-3xl font-bold text-white">
                              <AnimatedCounter end={2} suffix=" мин" />
                            </span>
                          </div>
                          <div className="w-full bg-white/15 rounded-full h-4 overflow-hidden">
                            <div 
                              className="bg-gradient-to-r from-yellow-400 to-orange-500 h-4 rounded-full animate-chart-fill" 
                              style={{ width: '95%' }}
                            ></div>
                          </div>
                          <div className="flex justify-between items-center mt-6">
                            <span className="text-base text-white/70">Традиционный анализ</span>
                            <span className="text-2xl text-white/60">2-5 дней</span>
                          </div>
                          <div className="w-full bg-white/15 rounded-full h-3 overflow-hidden">
                            <div 
                              className="bg-white/40 h-3 rounded-full animate-chart-fill-delayed" 
                              style={{ width: '15%' }}
                            ></div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* График 3: Охват */}
                    <div className="w-full flex-shrink-0 px-4">
                      <div className="liquid-glass-card rounded-3xl p-8 space-y-6 max-w-2xl mx-auto">
                        <div className="relative z-10 flex items-center gap-3 justify-center">
                          <Activity className="w-8 h-8 text-blue-400" />
                          <h4 className="text-2xl font-medium text-white">Охват</h4>
                        </div>
                        <div className="space-y-4">
                          <div className="flex justify-between items-center">
                            <span className="text-base text-white/70">Параметров анализа</span>
                            <span className="text-3xl font-bold text-white">
                              <AnimatedCounter end={500} suffix="+" />
                            </span>
                          </div>
                          <div className="w-full bg-white/15 rounded-full h-4 overflow-hidden">
                            <div 
                              className="bg-gradient-to-r from-blue-400 to-cyan-500 h-4 rounded-full animate-chart-fill" 
                              style={{ width: '100%' }}
                            ></div>
                          </div>
                          <div className="flex justify-between items-center mt-6">
                            <span className="text-base text-white/70">Стандартные тесты</span>
                            <span className="text-2xl text-white/60">50-100</span>
                          </div>
                          <div className="w-full bg-white/15 rounded-full h-3 overflow-hidden">
                            <div 
                              className="bg-white/40 h-3 rounded-full animate-chart-fill-delayed" 
                              style={{ width: '20%' }}
                            ></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Индикаторы */}
                <div className="flex justify-center gap-2 mt-6">
                  {[0, 1, 2].map((index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentMetric(index)}
                      className={`h-2 rounded-full transition-all duration-300 ${
                        index === currentMetric
                          ? 'w-8 bg-white'
                          : 'w-2 bg-white/40 hover:bg-white/60'
                      }`}
                    />
                  ))}
                </div>
              </div>

              {/* Статистика */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">
                <div className="glass-stat rounded-3xl p-5 sm:p-6 text-center space-y-2 hover:scale-[1.02] transition-transform">
                  <p className="relative z-10 text-3xl md:text-4xl font-bold text-white">
                    <AnimatedCounter end={5000} suffix="+" />
                  </p>
                  <p className="relative z-10 text-sm text-blue-200/80">Анализов обработано</p>
                </div>
                <div className="glass-stat rounded-3xl p-5 sm:p-6 text-center space-y-2 hover:scale-[1.02] transition-transform">
                  <p className="relative z-10 text-3xl md:text-4xl font-bold text-white">
                    <AnimatedCounter end={96.5} suffix="%" />
                  </p>
                  <p className="relative z-10 text-sm text-green-200/80">Точность AI</p>
                </div>
                <div className="glass-stat rounded-3xl p-5 sm:p-6 text-center space-y-2 hover:scale-[1.02] transition-transform">
                  <p className="relative z-10 text-3xl md:text-4xl font-bold text-white">
                    <AnimatedCounter end={2} suffix=" мин" />
                  </p>
                  <p className="relative z-10 text-sm text-purple-200/80">Время анализа</p>
                </div>
                <div className="glass-stat rounded-3xl p-5 sm:p-6 text-center space-y-2 hover:scale-[1.02] transition-transform">
                  <p className="relative z-10 text-3xl md:text-4xl font-bold text-white">24/7</p>
                  <p className="relative z-10 text-sm text-orange-200/80">Доступность</p>
                </div>
              </div>

              {/* Отзывы */}
              <div className="liquid-glass-card rounded-3xl p-6 md:p-8 space-y-6">
                <div className="relative z-10 text-center space-y-4 mb-6">
                  <h3 className="text-2xl md:text-3xl font-light text-white">
                    Отзывы пользователей
                  </h3>
                  <p className="text-base text-white/70">
                    Что говорят о нас наши клиенты
                  </p>
                </div>

                {/* Карусель отзывов */}
                {reviews.length > 0 ? (
                  <div className="relative">
                    <div className="overflow-hidden">
                      <div className="flex transition-transform duration-500" style={{ transform: `translateX(-${currentReview * 100}%)` }}>
                        {reviews.map((review, index) => (
                          <div key={review.id} className="w-full flex-shrink-0 px-4">
                            <div className="liquid-glass-card rounded-3xl p-6 md:p-8 space-y-4 max-w-2xl mx-auto">
                              <div className="flex gap-1">
                                {[...Array(5)].map((_, i) => (
                                  <span key={i} className={`text-2xl ${i < review.rating ? 'text-yellow-400' : 'text-white/20'}`}>★</span>
                                ))}
                              </div>
                              <p className="text-base md:text-lg text-white/80 italic leading-relaxed">
                                &ldquo;{review.comment}&rdquo;
                              </p>
                              <p className="text-sm text-white/60 font-medium">— {review.name}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Навигация */}
                    {reviews.length > 1 && (
                      <div className="flex items-center justify-center gap-4 mt-6">
                        <button
                          onClick={() => setCurrentReview((prev) => (prev - 1 + reviews.length) % reviews.length)}
                          className="glass-btn text-white p-3 rounded-full"
                        >
                          <ChevronLeft className="w-5 h-5" />
                        </button>
                        
                        <div className="flex gap-2">
                          {reviews.map((_, index) => (
                            <button
                              key={index}
                              onClick={() => setCurrentReview(index)}
                              className={`h-2 rounded-full transition-all duration-300 ${
                                index === currentReview
                                  ? 'w-8 bg-white'
                                  : 'w-2 bg-white/40 hover:bg-white/60'
                              }`}
                            />
                          ))}
                        </div>
                        
                        <button
                          onClick={() => setCurrentReview((prev) => (prev + 1) % reviews.length)}
                          className="glass-btn text-white p-3 rounded-full"
                        >
                          <ChevronRight className="w-5 h-5" />
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-center text-white/60">Пока нет отзывов. Будьте первым!</p>
                )}

                <div className="relative z-10 text-center mt-8">
                  <Button
                    onClick={() => setShowReviewForm(!showReviewForm)}
                    className="glass-btn text-white px-6 py-3 rounded-full hover:scale-105"
                    data-testid="toggle-review-form-btn"
                  >
                    <Plus className="w-5 h-5 mr-2" />
                    Оставить отзыв
                  </Button>
                </div>

                {showReviewForm && (
                  <div className="liquid-glass-card rounded-3xl p-6 mt-6 space-y-4">
                    <h4 className="text-lg font-medium text-white">Ваш отзыв</h4>
                    <form onSubmit={handleSubmitReview} className="space-y-4">
                      <div>
                        <label className="text-sm text-white/70 block mb-2">Имя</label>
                        <input
                          type="text"
                          required
                          value={reviewForm.name}
                          onChange={(e) => setReviewForm({...reviewForm, name: e.target.value})}
                          className="w-full liquid-glass-icon rounded-2xl px-4 py-3 text-white placeholder-white/50 focus:outline-none focus:border-white/30"
                          placeholder="Ваше имя"
                          data-testid="review-name-input"
                        />
                      </div>
                      <div>
                        <label className="text-sm text-white/70 block mb-2">Email</label>
                        <input
                          type="email"
                          required
                          value={reviewForm.email}
                          onChange={(e) => setReviewForm({...reviewForm, email: e.target.value})}
                          className="w-full liquid-glass-icon rounded-2xl px-4 py-3 text-white placeholder-white/50 focus:outline-none focus:border-white/30"
                          placeholder="your@email.com"
                          data-testid="review-email-input"
                        />
                      </div>
                      <div>
                        <label className="text-sm text-white/70 block mb-2">Оценка</label>
                        <div className="flex gap-2">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <button
                              key={star}
                              type="button"
                              onClick={() => setReviewForm({...reviewForm, rating: star})}
                              className="text-3xl transition-colors"
                            >
                              <span className={star <= reviewForm.rating ? 'text-yellow-400' : 'text-white/20'}>★</span>
                            </button>
                          ))}
                        </div>
                      </div>
                      <div>
                        <label className="text-sm text-white/70 block mb-2">Комментарий</label>
                        <textarea
                          required
                          value={reviewForm.comment}
                          onChange={(e) => setReviewForm({...reviewForm, comment: e.target.value})}
                          className="w-full liquid-glass-icon rounded-2xl px-4 py-3 text-white placeholder-white/50 focus:outline-none focus:border-white/30 min-h-[100px]"
                          placeholder="Поделитесь вашим опытом..."
                          data-testid="review-comment-input"
                        />
                      </div>
                      <div className="flex gap-3">
                        <Button
                          type="submit"
                          className="flex-1 glass-btn-accent text-white rounded-full py-5"
                          data-testid="review-submit-btn"
                        >
                          Отправить
                        </Button>
                        <Button
                          type="button"
                          onClick={() => setShowReviewForm(false)}
                          className="flex-1 glass-btn text-white rounded-full py-5"
                          data-testid="review-cancel-btn"
                        >
                          Отмена
                        </Button>
                      </div>
                    </form>
                  </div>
                )}
              </div>

              {/* CTA секция */}
              <div className="text-center space-y-6 py-8">
                <div className="liquid-glass-card rounded-3xl p-8 md:p-12 space-y-6">
                  <h3 className="relative z-10 text-2xl md:text-3xl font-light text-white">
                    Попробуйте AIMedGuru прямо сейчас
                  </h3>
                  <p className="relative z-10 text-base text-white/70 max-w-2xl mx-auto">
                    Получите профессиональный анализ ваших медицинских данных за считанные минуты
                  </p>
                  <div className="relative z-10 flex justify-center mt-6">
                    <Button
                      onClick={() => navigate('/auth')}
                      className="glass-btn-accent text-white px-8 py-5 text-base rounded-full transition-all duration-300 hover:scale-105 font-medium"
                      data-testid="start-free-btn"
                    >
                      Начать бесплатно
                    </Button>
                  </div>
                </div>
              </div>

            </div>
          </div>

          {/* Footer — RF 2026 legal compliance, full-bleed Apple Liquid Glass */}
          <footer className="legal-glass-footer relative w-full mt-16" data-testid="landing-footer">
            <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-10 py-12 md:py-16">

              {/* Top row: 4-column grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">

                {/* 1. Brand & legal entity */}
                <div className="space-y-4">
                  <h4 className="text-base font-medium text-white tracking-wide">AIMedGuru</h4>
                  <p className="text-sm text-white/60 leading-relaxed">
                    Информационно-справочный ИИ-сервис для предварительного анализа медицинских документов. Не является медицинским изделием и не оказывает медицинских услуг.
                  </p>

                  <div className="space-y-2 pt-2 text-xs text-white/55">
                    <div className="flex items-start gap-2">
                      <Building2 className="w-3.5 h-3.5 mt-0.5 text-white/40 flex-shrink-0" />
                      <div>
                        <div className="text-white/75">ООО «АИМЕДГУРУ»</div>
                        <div>ИНН: 7700000000</div>
                        <div>ОГРН: 1000000000000</div>
                      </div>
                    </div>
                    <div className="flex items-start gap-2">
                      <MapPin className="w-3.5 h-3.5 mt-0.5 text-white/40 flex-shrink-0" />
                      <span>123000, г. Москва,<br />внутригородская территория</span>
                    </div>
                  </div>
                </div>

                {/* 2. Documents */}
                <div className="space-y-4">
                  <h4 className="text-base font-medium text-white tracking-wide flex items-center gap-2">
                    <Scale className="w-4 h-4 text-white/50" />
                    Правовые документы
                  </h4>
                  <ul className="space-y-2.5 text-sm">
                    <li>
                      <button
                        onClick={() => setLegalDoc({ document: PRIVACY_POLICY, title: 'Политика конфиденциальности' })}
                        className="legal-link text-left"
                        data-testid="footer-privacy-link"
                      >
                        Политика конфиденциальности
                      </button>
                    </li>
                    <li>
                      <button
                        onClick={() => setLegalDoc({ document: USER_AGREEMENT, title: 'Пользовательское соглашение' })}
                        className="legal-link text-left"
                        data-testid="footer-user-agreement-link"
                      >
                        Пользовательское соглашение
                      </button>
                    </li>
                    <li>
                      <button
                        onClick={() => setLegalDoc({ document: LICENSE_AGREEMENT, title: 'Лицензионное соглашение' })}
                        className="legal-link text-left"
                        data-testid="footer-license-link"
                      >
                        Лицензионное соглашение
                      </button>
                    </li>
                    <li>
                      <button
                        onClick={() => setLegalDoc({ document: MEDICAL_DATA_CONSENT, title: 'Согласие на обработку медицинских данных' })}
                        className="legal-link text-left"
                        data-testid="footer-medical-consent-link"
                      >
                        Согласие на обработку медицинских данных
                      </button>
                    </li>
                  </ul>

                  <div className="pt-3 text-xs text-white/45 leading-relaxed">
                    Регулируется ФЗ № 152-ФЗ «О персональных данных», ФЗ № 323-ФЗ «Об основах охраны здоровья», ФЗ № 149-ФЗ «Об информации».
                  </div>
                </div>

                {/* 3. Contacts */}
                <div className="space-y-4">
                  <h4 className="text-base font-medium text-white tracking-wide">Контакты</h4>
                  <ul className="space-y-3 text-sm text-white/65">
                    <li className="flex items-start gap-2">
                      <Mail className="w-3.5 h-3.5 mt-1 text-white/40 flex-shrink-0" />
                      <div>
                        <div className="text-white/45 text-xs">Поддержка</div>
                        <a href="mailto:support@aimedguru.ru" className="legal-link">support@aimedguru.ru</a>
                      </div>
                    </li>
                    <li className="flex items-start gap-2">
                      <Mail className="w-3.5 h-3.5 mt-1 text-white/40 flex-shrink-0" />
                      <div>
                        <div className="text-white/45 text-xs">По вопросам персональных данных</div>
                        <a href="mailto:privacy@aimedguru.ru" className="legal-link">privacy@aimedguru.ru</a>
                      </div>
                    </li>
                    <li className="flex items-start gap-2">
                      <Mail className="w-3.5 h-3.5 mt-1 text-white/40 flex-shrink-0" />
                      <div>
                        <div className="text-white/45 text-xs">Юридические запросы</div>
                        <a href="mailto:legal@aimedguru.ru" className="legal-link">legal@aimedguru.ru</a>
                      </div>
                    </li>
                  </ul>

                  <div className="pt-2 text-xs text-white/45">
                    Ответственный за организацию обработки ПДн назначен. Уведомление о включении в Реестр операторов ПДн направлено в Роскомнадзор.
                  </div>

                  {/* Кнопка "Запрос на удаление данных" — реализация прав субъекта ПДн (ст. 14 ФЗ-152) */}
                  <button
                    onClick={() => setShowDataDeletionModal(true)}
                    className="glass-btn w-full text-white text-xs rounded-full px-4 py-2.5 mt-2 flex items-center justify-center gap-2 hover:scale-[1.02] transition-transform"
                    data-testid="footer-data-deletion-btn"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    Запрос на удаление данных
                  </button>
                </div>

                {/* 4. Compliance & disclaimers */}
                <div className="space-y-4">
                  <h4 className="text-base font-medium text-white tracking-wide">Безопасность и соответствие</h4>
                  <ul className="space-y-2.5 text-sm text-white/65">
                    <li className="flex items-start gap-2">
                      <ShieldCheck className="w-4 h-4 text-emerald-400/80 mt-0.5 flex-shrink-0" />
                      <span>Шифрование данных TLS 1.3 и AES-256 при хранении</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <ShieldCheck className="w-4 h-4 text-emerald-400/80 mt-0.5 flex-shrink-0" />
                      <span>Хранение ПДн на территории РФ (Постановление № 1119)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <ShieldCheck className="w-4 h-4 text-emerald-400/80 mt-0.5 flex-shrink-0" />
                      <span>Уведомление о компьютерных инцидентах в течение 24 часов</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <ShieldCheck className="w-4 h-4 text-emerald-400/80 mt-0.5 flex-shrink-0" />
                      <span>Возрастная маркировка: 18+</span>
                    </li>
                  </ul>
                </div>
              </div>

              {/* AI / Medical disclaimer block */}
              <div className="mt-12 pt-8 border-t border-white/10">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 text-xs text-white/55 leading-relaxed">
                  <div className="space-y-2">
                    <div className="text-white/75 font-medium uppercase tracking-wider text-[11px]">Маркировка ИИ-контента</div>
                    <p>
                      Контент в Сервисе генерируется системой искусственного интеллекта (LLM). Результаты носят информационно-справочный характер и могут содержать неточности. Рекомендации Минцифры РФ по обозначению ИИ-контента (2025–2026) соблюдаются.
                    </p>
                  </div>
                  <div className="space-y-2">
                    <div className="text-white/75 font-medium uppercase tracking-wider text-[11px]">Медицинский дисклеймер</div>
                    <p>
                      Сервис не является медицинской организацией, медицинским изделием или средством диагностики. Информация не заменяет очной консультации врача. В экстренных случаях звоните 103 или 112.
                    </p>
                  </div>
                </div>
              </div>

              {/* Bottom bar */}
              <div className="mt-10 pt-6 border-t border-white/10">
                <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-xs text-white/45">
                  <p>© 2026 ООО «АИМЕДГУРУ». Все права защищены.</p>
                  <p>
                    Редакция документов от 22.01.2026 г.
                  </p>
                </div>
              </div>
            </div>
          </footer>
        </div>

      {/* Модальное окно соглашения */}
      {showAgreement && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70">
          <Card className="w-full max-w-md liquid-glass-card shadow-2xl rounded-2xl overflow-hidden animate-scale-in">
            <div className="flex items-center justify-between p-4 border-b border-white/10">
              <h2 className="text-lg font-light text-white">
                Соглашение
              </h2>
              <button
                onClick={() => setShowAgreement(false)}
                className="text-white/60 hover:text-white transition-colors"
                data-testid="agreement-close-btn"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <ScrollArea 
              ref={scrollAreaRef}
              className="h-[50vh] sm:h-[60vh] px-4"
              onScroll={handleScrollCheck}
            >
              <div className="space-y-4 py-4 pr-4">
                {LICENSE_AGREEMENT.sections.slice(0, 5).map((section, index) => (
                  <div key={index} className="space-y-2">
                    <h3 className="text-sm font-medium text-white/90">
                      {section.title}
                    </h3>
                    <p className="text-xs text-white/70 leading-relaxed">
                      {section.content}
                    </p>
                  </div>
                ))}
              </div>
            </ScrollArea>

            <div className="p-4 border-t border-white/10 space-y-3">
              <label className="flex items-start gap-3 cursor-pointer">
                <Checkbox
                  id="terms"
                  checked={agreedToTerms}
                  onCheckedChange={(checked) => setAgreedToTerms(checked)}
                  className="mt-1 border-white/30 data-[state=checked]:bg-white data-[state=checked]:text-black"
                />
                <span className="text-xs text-white/70 leading-relaxed select-none">
                  Я прочитал(а) и согласен(на) с условиями лицензионного соглашения. 
                  Я понимаю, что данный сервис не предоставляет медицинские услуги.
                </span>
              </label>

              <Button
                onClick={handleConfirmAgreement}
                disabled={!agreedToTerms}
                className="w-full glass-btn disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-full transition-all duration-300"
                data-testid="agreement-continue-btn"
              >
                Продолжить
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Модальное окно для глубокого анализа */}
      <DeepAnalysisModal 
        isOpen={showAnalysisModal} 
        onClose={() => setShowAnalysisModal(false)} 
      />

      {/* Document Example Modal */}
      <DocumentExampleModal
        document={selectedDocument}
        isOpen={!!selectedDocument}
        onClose={() => setSelectedDocument(null)}
      />

      {/* Legal Modal — открывается из футера */}
      <LegalModal
        isOpen={!!legalDoc}
        onClose={() => setLegalDoc(null)}
        document={legalDoc?.document}
        title={legalDoc?.title}
      />

      {/* Data Deletion Request Modal — реализация прав субъекта ПДн по ФЗ-152 */}
      <DataDeletionRequestModal
        isOpen={showDataDeletionModal}
        onClose={() => setShowDataDeletionModal(false)}
      />

      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        
        @keyframes fadeOut {
          from {
            opacity: 1;
          }
          to {
            opacity: 0;
          }
        }

        @keyframes chartFill {
          from {
            width: 0%;
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        .animate-chart-fill {
          animation: chartFill 1.5s ease-out forwards;
        }

        .animate-chart-fill-delayed {
          animation: chartFill 1.5s ease-out 0.3s forwards;
          width: 0%;
        }
      `}</style>
    </div>
  );
};

export default LandingPage;